import React, { useState, useEffect, useMemo } from "react";
import LoadingSpinner from "../../composant_reutilisable/loading";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowRight, FaBox, FaBoxOpen, FaWeightHanging, FaMapMarkerAlt, FaCalendarAlt, FaShip, FaLayerGroup } from "react-icons/fa";
import { useLocation, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";


const Liste_frets_ecran = ({ titre }) => {
  const location = useLocation();
  // const { nom_parc } = location.state || {}; // Obtention de le reference du parc
  const { parc_ref } = useParams();
  const [frets, setFrets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // 👈 état recherche

  const [parcs, setParcs] = useState([]);
  // Récupération de tous les parcs
  const fetchParcs = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(process.env.REACT_APP_HOTE + "/liste-parc/");
      if (response.ok) {
        const data = await response.json();
        setParcs(data);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des parcs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  //Récupération de toutes les catégories
  const [selectedCategorie, setSelectedCategorie] = useState(null); // null = pas de filtre
  const [allCategories, setAllCategories] = useState([]);
  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(process.env.REACT_APP_HOTE + "/liste-categories/");
      if (response.ok) {
        const data = await response.json();
        setAllCategories(data);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Détecter mobile et tablette
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1024 });

  // SSE : écouter les frets en temps réel avec reconnexion automatique

  useEffect(() => {
    if (!parc_ref) return;

    let evtSource;
    let reconnectTimer;
    const RECONNECT_DELAY = 5000; // 5 secondes avant de retenter

    const connect = () => {
      // console.log("🔗 Tentative de connexion SSE...");

      evtSource = new EventSource(`${process.env.REACT_APP_HOTE}/frets-stream/${parc_ref}/`);

      evtSource.onopen = () => {
        // console.log("✅ Connexion SSE ouverte :", evtSource.url);
      };

      // ✅ Ajout ou modification
      evtSource.onmessage = (e) => {
        if (e.data === "ping") return; // garder la connexion active sans traiter

        try {
          const fret = JSON.parse(e.data);
          // console.log("📦 Fret reçu :", fret);

          setFrets((prev) => {
            const existing = prev.find((f) => f.id === fret.id);
            if (existing) {
              // Modification → remplacement
              return prev.map((f) => (f.id === fret.id ? fret : f));
            } else {
              // Nouveau → ajout
              return [fret, ...prev];
            }
          });
        } catch (err) {
          console.error("Erreur parsing fret SSE:", err);
        }
      };

      // ✅ Suppression
      evtSource.addEventListener("delete", (e) => {
        const deletedId = parseInt(e.data, 10);
        // console.log("❌ Fret supprimé :", deletedId);
        setFrets((prev) => prev.filter((f) => f.id !== deletedId));
      });

      evtSource.onerror = (err) => {
        console.error("⚠️ Erreur SSE :", err);

        // Si la connexion est fermée ou échouée → on retente
        if (evtSource.readyState === EventSource.CLOSED) {
          // console.log(`🔄 Tentative de reconnexion SSE dans ${RECONNECT_DELAY / 1000}s...`);
          evtSource.close();
          reconnectTimer = setTimeout(connect, RECONNECT_DELAY);
        }
      };
    };

    connect(); // première connexion

    return () => {
      // console.log("❌ Fermeture connexion SSE");
      if (evtSource) evtSource.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [parc_ref]);


  const fetchFrets = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_HOTE}/liste-frets-complet-categorie/${parc_ref}/`
      );
      const data = await response.json();
      setFrets(data.frets || []);
    } catch (error) {
      console.error("Erreur réseau:", error);
      setFrets([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFrets();
    fetchCategories();
    fetchParcs();
  }, []);

  // Trouver le parc correspondant à parc_ref
  const parc = parcs.find((p) => p.ref_parc === parc_ref);

  // 🔎 Filtrer frets avant regroupement
  const filteredFrets = useMemo(() => {
    if (!searchTerm) return frets;
    const lowerSearch = searchTerm.toLowerCase();

    return frets.filter(fret => {
      const matchRef = (fret.ref || "").toLowerCase().includes(lowerSearch);
      const matchNature = (fret.nature_fret || "").toLowerCase().includes(lowerSearch);
      const matchDate = (fret.date_creation || "").toLowerCase().includes(lowerSearch);
      const matchVilleDepart = (fret.ville_depart || "").toLowerCase().includes(lowerSearch);
      const matchVilleArrivee = (fret.ville_arrivee || "").toLowerCase().includes(lowerSearch);

      // Vérifier dans les lots
      const matchLots = Array.isArray(fret.lots) && fret.lots.some(lot =>
        (lot.reference || "").toLowerCase().includes(lowerSearch) ||
        (lot.date_creation || "").toLowerCase().includes(lowerSearch) ||
        (lot.containers || []).some(container =>
          (container.ref || "").toLowerCase().includes(lowerSearch)
        ) ||
        (lot.marchandises || []).some(m =>
          (m.nom || "").toLowerCase().includes(lowerSearch)
        )
      );

      // Vérifier dans les marchandises (si directement sur fret)
      const matchMarchandises = Array.isArray(fret.marchandises) && fret.marchandises.some(m =>
        (m.nom || "").toLowerCase().includes(lowerSearch)
      );

      return (
        matchRef ||
        matchNature ||
        matchDate ||
        matchVilleDepart ||
        matchVilleArrivee ||
        matchLots ||
        matchMarchandises
      );
    });
  }, [frets, searchTerm]);

  // Déterminer si on est en mode recherche
  const isSearching = searchTerm.trim() !== "";

  // Regrouper camions par catégorie
  // const fretsByCategory = useMemo(() => {

  //   return allCategories.filter((cat) =>
  //     selectedCategorie ? cat.nom_categorie === selectedCategorie : true
  //   ).map((cat) => {
  //     const frets = filteredFrets.filter(c => c.categorie === cat.nom_categorie);
  //     return {
  //       nom: cat.nom_categorie,   // nom unique pour la catégorie
  //       frets                     // liste des frets dans cette catégorie
  //     };
  //   })
  //     .filter(cat => !isSearching || cat.frets.length > 0);
  // }, [allCategories, filteredFrets, selectedCategorie, isSearching]);



  const fretsByCategory = useMemo(() => {
    // Étape 1 : filtrer les catégories selon le filtre sélectionné
    const categoriesFiltrees = allCategories.filter((cat) =>
      selectedCategorie ? cat.nom_categorie === selectedCategorie : true
    );

    // Étape 2 : créer les objets catégorie avec leurs frets
    const categoriesAvecFrets = categoriesFiltrees.map((cat) => {
      const fretsAssocies = filteredFrets.filter(
        (f) => f.categorie === cat.nom_categorie
      );
      return {
        nom: cat.nom_categorie,
        frets: fretsAssocies,
      };
    });

    // Étape 3 : séparer celles qui ont des frets et celles qui n'en ont pas
    const categoriesAvecDonnees = categoriesAvecFrets.filter(
      (cat) => cat.frets.length > 0
    );
    const categoriesSansDonnees = categoriesAvecFrets.filter(
      (cat) => cat.frets.length === 0
    );

    // Étape 4 : fusionner (celles avec données d'abord)
    const resultat = [...categoriesAvecDonnees, ...categoriesSansDonnees];

    // Étape 5 : si recherche active → garder seulement celles avec frets
    return isSearching ? categoriesAvecDonnees : resultat;
  }, [allCategories, filteredFrets, selectedCategorie, isSearching]);


  const dureeAffichageCategories = 7000; // 30s par exemple
  const [categorieIndex, setCategorieIndex] = useState(0);
  // Avancer automatiquement toutes les 30s
  useEffect(() => {
    if (!fretsByCategory || fretsByCategory.length === 0 || isPaused || searchTerm || selectedCategorie) return;

    const interval = setInterval(() => {
      setCategorieIndex((prevIndex) =>
        (prevIndex + 3) % fretsByCategory.length
      );
    }, dureeAffichageCategories);

    return () => clearInterval(interval);
  }, [fretsByCategory, isPaused, searchTerm, selectedCategorie]);

  // Récupérer les 3 catégories à afficher
  const visibleCategories = fretsByCategory.slice(categorieIndex, categorieIndex + 3);


  // Si on arrive en fin de tableau, on reprend au début
  if (visibleCategories.length < 3 && fretsByCategory.length > 1) {
    visibleCategories.push(...fretsByCategory.slice(0, 3 - visibleCategories.length));
  }

  const formatDate = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculerPoidsTotalLot = (lot) => {
    if (!lot || !lot.marchandises) return 0;
    return lot.marchandises.reduce((total, marchandise) => {
      return total + (marchandise.poids || 0);
    }, 0);
  };

  const calculerPoidsTotalFret = (fret) => {
    if (!fret || !fret.lots) return 0;
    return fret.lots.reduce((total, lot) => {
      return total + calculerPoidsTotalLot(lot);
    }, 0);
  };

  if (isLoading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)"
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCategorieIndex(0);
  };


  const CategoryColumn = ({ category, frets }) => {
    const [currentFretIndex, setCurrentFretIndex] = useState(0);
    const [currentLotIndex, setCurrentLotIndex] = useState(0);
    const [direction, setDirection] = useState(1); // 1 = droite, -1 = gauche

    // Défilement automatique
    useEffect(() => {
      if (isPaused || frets.length === 0 || searchTerm) return;

      const interval = setInterval(() => {
        const currentFret = frets[currentFretIndex];

        if (currentFret && currentFret.lots && currentFret.lots.length > 0) {
          if (currentLotIndex < currentFret.lots.length - 1) {
            setCurrentLotIndex((prev) => prev + 1);
          } else {
            setDirection(1);
            setCurrentFretIndex((prev) => (prev + 1) % frets.length);
            setCurrentLotIndex(0);
          }
        } else {
          setDirection(1);
          setCurrentFretIndex((prev) => (prev + 1) % frets.length);
          setCurrentLotIndex(0);
        }
      }, 2000);

      return () => clearInterval(interval);
    }, [frets, currentFretIndex, currentLotIndex, isPaused, searchTerm]);

    // ⚠️ Si aucun fret → on retourne directement le "aucun fret"
    if (!frets || frets.length === 0) {
      return (
        <div
          style={{
            flex: "1",
            minWidth: "300px",
            maxWidth: "400px",
            padding: "20px",
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
            border: "1px solid #e2e8f0",
            margin: "10px",
            position: "relative",
          }}
        >
          <div
            style={{
              textAlign: "center",
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#3b82f6",
              color: "white",
              borderRadius: "12px",
              margin: "-20px -20px 20px -20px",
            }}
          >
            <span style={{ fontSize: "16px", fontWeight: "600" }}>{category}</span>
            <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "4px" }}>
              Aucun fret
            </div>
          </div>
          <div
            style={{
              padding: "30px",
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "14px",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px dashed #cbd5e1",
            }}
          >
            Aucun fret déclaré
          </div>
        </div>
      );
    }

    const currentFret = frets[currentFretIndex];
    if (!currentFret) return null;

    const currentLot = currentFret.lots && currentFret.lots.length > 0
      ? currentFret.lots[currentLotIndex]
      : null;

    return (
      <div
        style={{
          flex: "1",
          minWidth: "300px",
          maxWidth: "400px",
          padding: "20px",
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e2e8f0",
          margin: "10px",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* En-tête de catégorie */}
        <div style={{
          textAlign: "center",
          marginBottom: "20px",
          padding: "12px",
          backgroundColor: "#3b82f6",
          color: "white",
          borderRadius: "12px",
          margin: "-20px -20px 20px -20px",
          zIndex: 10,
          position: "relative"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <FaLayerGroup size={16} />
            <span style={{ fontSize: "16px", fontWeight: "600" }}>
              {category}
            </span>
          </div>
          <div style={{ fontSize: "12px", opacity: 0.9, marginTop: "4px" }}>
            {currentFretIndex + 1}/{frets.length} fret(s)
          </div>
        </div>

        {/* Carte du fret actuel avec animation de swipe */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentFret.id}
            custom={direction}
            initial={{
              opacity: 0,
              x: direction > 0 ? 100 : -100
            }}
            animate={{
              opacity: 1,
              x: 0,
              transition: {
                duration: 0.5,
                ease: "easeOut"
              }
            }}
            exit={{
              opacity: 0,
              x: direction > 0 ? -100 : 100,
              transition: {
                duration: 0.5,
                ease: "easeIn"
              }
            }}
            style={{
              position: "relative",
              zIndex: 1
            }}
          >
            {/* Header du fret */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "16px"
            }}>
              <div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px"
                }}>
                  <FaShip size={14} color="#3b82f6" />
                  <span style={{
                    fontWeight: "600",
                    fontSize: "16px",
                    color: "#1e293b"
                  }}>
                    {currentFret.ref}
                  </span>
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  color: "#64748b"
                }}>
                  <FaCalendarAlt size={10} />
                  <span>{formatDate(currentFret.date_creation)}</span>
                </div>
              </div>
            </div>

            {/* Route */}
            <div style={{
              marginBottom: "16px"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px",
                backgroundColor: "#f8fafc",
                borderRadius: "10px",
                border: "1px solid #e2e8f0"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <FaMapMarkerAlt size={12} color="#ef4444" />
                  <span style={{ fontWeight: "600", fontSize: "12px", color: "#1e293b" }}>
                    {currentFret.ville_depart}
                  </span>
                </div>

                <FaArrowRight size={10} color="#64748b" />

                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <FaMapMarkerAlt size={12} color="#10b981" />
                  <span style={{ fontWeight: "600", fontSize: "12px", color: "#1e293b" }}>
                    {currentFret.ville_arrivee}
                  </span>
                </div>
              </div>
            </div>

            {/* Poids total */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "16px",
              padding: "10px 12px",
              backgroundColor: "#fffbeb",
              borderRadius: "8px",
              border: "1px solid #fef3c7"
            }}>
              <FaWeightHanging size={14} color="#f59e0b" />
              <span style={{ fontSize: "12px", color: "#92400e", fontWeight: "500" }}>
                {calculerPoidsTotalFret(currentFret)} kg
              </span>
            </div>

            {/* Section des lots avec animation */}
            <div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
                padding: "8px 0",
                borderBottom: "1px solid #e2e8f0"
              }}>
                <FaBox size={14} color="#3b82f6" />
                <span style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
                  Lots ({currentFret.lots?.length || 0})
                </span>
                {currentFret.lots && currentFret.lots.length > 0 && (
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    {currentLotIndex + 1}/{currentFret.lots.length}
                  </span>
                )}
              </div>

              <AnimatePresence mode="wait">
                {currentLot ? (
                  <motion.div
                    key={currentLot.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.3, ease: "easeOut" }
                    }}
                    exit={{
                      opacity: 0,
                      y: -20,
                      transition: { duration: 0.2, ease: "easeIn" }
                    }}
                    style={{
                      padding: "12px",
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "1px solid #f1f5f9",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
                    }}
                  >
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <FaBoxOpen size={12} color="#3b82f6" />
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                          {currentLot.reference}
                        </span>
                      </div>
                      <span style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#3b82f6",
                        padding: "4px 8px",
                        backgroundColor: "#eff6ff",
                        borderRadius: "10px"
                      }}>
                        {calculerPoidsTotalLot(currentLot)} kg
                      </span>
                    </div>
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "12px",
                      color: "#64748b",
                      marginBottom: '20px'
                    }}>
                      <FaCalendarAlt size={10} />
                      <span>{formatDate(currentLot.date_creation)}</span>
                    </div>

                    {/* Détails du lot */}
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      fontSize: "11px",
                      color: "#64748b"
                    }}>
                      {currentLot.containers && currentLot.containers.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <span>📦</span>
                          <span>{currentLot.containers.length} container(s)</span>
                        </div>
                      )}
                      {currentLot.marchandises && currentLot.marchandises.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <span>📋</span>
                          <span>{currentLot.marchandises.length} marchandise(s)</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: "12px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                      border: "1px dashed #cbd5e1"
                    }}
                  >
                    Aucun lot disponible
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Indicateurs de progression */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "6px",
          marginTop: "20px",
          paddingTop: "15px",
          borderTop: "1px solid #e2e8f0"
        }}>
          {frets.map((_, index) => (
            <div
              key={index}
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: index === currentFretIndex ? "#3b82f6" : "#e2e8f0",
                transition: "background-color 0.3s ease",
                cursor: "pointer"
              }}
              onClick={() => {
                setDirection(index > currentFretIndex ? 1 : -1);
                setCurrentFretIndex(index);
                setCurrentLotIndex(0);
              }}
            />
          ))}
        </div>
      </div>
    );
  };



  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        padding: "20px",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* En-tête principal */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          textAlign: "center",
          marginBottom: "30px"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
          <FaShip size={28} color="#3b82f6" />
          <h1 style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#1e293b",
            letterSpacing: "-0.02em",
            margin: 0
          }}>
            {parc ? parc.nom_parc : "Parc introuvable"}
          </h1>
        </div>
        <p style={{ color: "#64748b", fontSize: "14px", marginTop: "8px" }}>
          Affichage en temps réel des frets par catégorie
        </p>
      </motion.div>

      {/* 🔎 Barre de recherche visible uniquement en mobile/tablette */}
      {(isMobile || isTablet) && (
        <div style={{
          margin: "0 0 20px 0",
          padding: "0 10px",
          width: "100%",
          boxSizing: "border-box",
        }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: "10px",
              width: "100%",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
              }}
            >
              <input
                type="text"
                placeholder="Rechercher fret..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  borderRadius: "6px",
                  border: "1px solid #3498db",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    fontSize: "18px",
                    cursor: "pointer",
                    color: "#7f8c8d",
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Filtre catégories */}
            <select
              value={selectedCategorie || ""}
              onChange={(e) => setSelectedCategorie(e.target.value || null)}
              style={{
                width: "100%",
                padding: "12px 15px",
                borderRadius: "6px",
                border: "1px solid #3498db",
                fontSize: "14px",
                fontFamily: "inherit",
              }}
            >
              <option value="">Toutes les catégories</option>
              {allCategories.map((parc) => (
                <option key={parc.ref_parc} value={parc.nom_categorie}>
                  {parc.nom_categorie}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Grille des catégories */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "20px",
        margin: "0 auto",
        maxWidth: "1400px"
      }}
        // onMouseEnter={() => setIsPaused(true)}
        // onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        {visibleCategories.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: "30px",
            background: "#f9fafb",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
            color: "#64748b",
            fontStyle: "italic",
            fontSize: "16px"
          }}>
            Aucun catégorie trouvé
          </div>
        ) : (
          visibleCategories.map(cat => (
            <CategoryColumn
              key={cat.nom}
              category={cat.nom}
              frets={cat.frets}
            />
          ))
        )}
      </div>

    </div>
  );
};

export default Liste_frets_ecran;